import { useState } from 'react';
import { Sparkles, Loader2, Eye, Save, Play } from 'lucide-react';
import apiClient from '../utils/axios';

export default function AICampaignGenerator({ onSequenceGenerated }) {
  const [prompt, setPrompt] = useState('');
  const [numSteps, setNumSteps] = useState(3);
  const [targetAudience, setTargetAudience] = useState({
    industry: '',
    jobTitles: [],
    painPoints: []
  });
  const [generating, setGenerating] = useState(false);
  const [generatedSequence, setGeneratedSequence] = useState(null);
  const [previewStep, setPreviewStep] = useState(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      alert('Please enter a campaign prompt');
      return;
    }

    try {
      setGenerating(true);
      const response = await apiClient.post('/nurture-sequences/generate', {
        prompt,
        numSteps,
        targetAudience
      });

      setGeneratedSequence(response.data.sequence);
      alert(`Sequence generated successfully! ${response.data.sequence.steps_count} steps created.`);
      
      if (onSequenceGenerated) {
        onSequenceGenerated(response.data.sequence);
      }
    } catch (error) {
      console.error('Error generating sequence:', error);
      alert(error.response?.data?.message || 'Error generating sequence');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generatedSequence) return;

    try {
      await apiClient.put(`/nurture-sequences/${generatedSequence.id}/activate`);
      alert('Sequence saved and activated!');
      if (onSequenceGenerated) {
        onSequenceGenerated(generatedSequence);
      }
    } catch (error) {
      console.error('Error saving sequence:', error);
      alert(error.response?.data?.message || 'Error saving sequence');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles size={24} className="text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-900">AI Campaign Generator</h2>
      </div>

      <div className="space-y-6">
        {/* Campaign Prompt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Campaign Goal / Prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., Educate prospects about modern UCaaS solutions and convert them to customers..."
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
          />
          <p className="mt-1 text-sm text-gray-500">
            Describe what you want this nurture sequence to accomplish. AI will generate a complete campaign.
          </p>
        </div>

        {/* Number of Steps */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Steps
          </label>
          <select
            value={numSteps}
            onChange={(e) => setNumSteps(parseInt(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={1}>1 Step (Direct Send)</option>
            <option value={2}>2 Steps</option>
            <option value={3}>3 Steps</option>
            <option value={4}>4 Steps</option>
            <option value={5}>5 Steps</option>
            <option value={6}>6 Steps</option>
            <option value={7}>7 Steps</option>
            <option value={8}>8 Steps</option>
            <option value={9}>9 Steps</option>
            <option value={10}>10 Steps</option>
          </select>
          <p className="mt-1 text-sm text-gray-500">
            {numSteps === 1 
              ? 'Single email send - perfect for one-time campaigns or direct outreach.'
              : `Multi-step sequence with ${numSteps} emails spaced over time.`}
          </p>
        </div>

        {/* Target Audience */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Audience (Optional)
          </label>
          <div className="space-y-3">
            <input
              type="text"
              value={targetAudience.industry}
              onChange={(e) => setTargetAudience({ ...targetAudience, industry: e.target.value })}
              placeholder="Industry (e.g., Technology, Healthcare)"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              value={targetAudience.jobTitles.join(', ')}
              onChange={(e) => setTargetAudience({ 
                ...targetAudience, 
                jobTitles: e.target.value.split(',').map(t => t.trim()).filter(Boolean)
              })}
              placeholder="Job Titles (comma-separated, e.g., CEO, CTO, VP)"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={generating || !prompt.trim()}
          className="w-full px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {generating ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Generating Campaign...
            </>
          ) : (
            <>
              <Sparkles size={20} />
              Generate Campaign
            </>
          )}
        </button>

        {/* Generated Sequence Preview */}
        {generatedSequence && (
          <div className="mt-6 border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Generated Sequence: {generatedSequence.name || 'Untitled'}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                >
                  <Save size={16} />
                  Save & Activate
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {generatedSequence.description || 'AI-generated nurture sequence'}
            </p>
            <p className="text-sm text-gray-500">
              {generatedSequence.steps_count} steps generated. View in Nurture Dashboard to see details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

